// v10/src/components/admin/AdminOpenFlights.js
// Vista para admin/instructor: vuelos abiertos (status='open') de la org,
// con acciones de "Cerrar delegado" (PATCH /complete) y "Anular" (PATCH /annul).
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Hourglass, Gauge, CheckCircle2, Ban, RefreshCw, Loader, X,
  Plane, User, Calendar, AlertTriangle, History, UserCheck, MessageSquare,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hobbsService } from '../../services/apiService';

// Umbral en minutos a partir del cual un vuelo abierto se considera "viejo"
// y se destaca con badge naranja. Consistente con el flujo del scheduler
// (HOBBS_NOTIFY_MINUTES default 60) pero usado solo para UX visual.
const STALE_MINUTES = 60;

const formatRelative = (iso) => {
  if (!iso) return '—';
  const now = Date.now();
  const t   = new Date(iso).getTime();
  const m   = Math.floor((now - t) / 60000);
  if (m < 1)   return 'recién';
  if (m < 60)  return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `hace ${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `hace ${d}d ${h % 24}h`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  return dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
       + ' ' + dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const minutesSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 60000) : 0;

// ── Modal: cerrar delegado ──────────────────────────────────────
const CloseModal = ({ flight, darkMode, onClose, onDone }) => {
  const [hobbsEnd, setHobbsEnd] = useState('');
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [warning, setWarning]   = useState('');

  const text  = darkMode ? 'white' : '#0f172a';
  const sub   = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const bg    = darkMode ? '#111827' : 'white';
  const input = {
    width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 15,
    fontWeight: 600, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    color: text,
  };

  const onHobbsChange = (v) => {
    setHobbsEnd(v);
    setError(''); setWarning('');
    const end   = parseFloat(v);
    const start = flight?.hobbs_start != null ? parseFloat(flight.hobbs_start) : null;
    if (!isNaN(end) && start != null && end < start) {
      setWarning(`El horómetro final (${end}) es menor que el inicial (${start}). Verificá la lectura.`);
    }
  };

  const submit = async () => {
    setError('');
    const end = parseFloat(hobbsEnd);
    if (hobbsEnd === '' || isNaN(end)) { setError('Ingresá el horómetro final'); return; }
    setSaving(true);
    try {
      await hobbsService.complete(flight.id, { hobbs_end: end, notes: notes || null });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al cerrar el vuelo');
    } finally { setSaving(false); }
  };

  const acName = `${flight.manufacturer} ${flight.model_name}${flight.registration ? ' (' + flight.registration + ')' : ''}`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)',
               backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center',
               justifyContent: 'center', padding: 16 }}>
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        style={{ width: '100%', maxWidth: 420, borderRadius: 24, background: bg,
                 boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                 border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                 maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '20px 24px 16px',
                      borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14,
                          background: 'linear-gradient(135deg,#16a34a,#15803d)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>Cerrar vuelo (delegado)</h2>
              <p style={{ margin: 0, fontSize: 12, color: sub }}>{acName}</p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none',
                     borderRadius: 10, width: 34, height: 34, cursor: 'pointer',
                     display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ margin: '16px 24px 0', padding: '10px 14px', borderRadius: 12,
                      background: darkMode ? 'rgba(37,99,235,0.1)' : 'rgba(219,234,254,0.6)',
                      border: darkMode ? '1px solid rgba(37,99,235,0.2)' : '1px solid rgba(37,99,235,0.15)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#93c5fd' : '#2563eb' }}>
            Horómetro inicial
          </span>
          <span style={{ fontSize: 16, fontWeight: 900, color: darkMode ? '#93c5fd' : '#1d4ed8' }}>
            {flight?.hobbs_start != null ? `${flight.hobbs_start} hs` : '—'}
          </span>
        </div>

        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                            display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
              <Gauge style={{ width: 13, height: 13 }} /> Horómetro al aterrizar *
            </label>
            <input
              type="number" step="0.1" inputMode="decimal"
              placeholder={flight?.hobbs_start != null
                ? `Ej: ${(parseFloat(flight.hobbs_start) + 1).toFixed(1)}`
                : 'Ej: 1235.7'}
              value={hobbsEnd}
              onChange={e => onHobbsChange(e.target.value)}
              style={input} />
            <p style={{ margin: '6px 2px 0', fontSize: 11, color: sub, lineHeight: 1.4 }}>
              El horómetro final es el valor actual que marca el instrumento del avión.
            </p>
            {warning && (
              <p style={{ margin: '6px 2px 0', fontSize: 11, fontWeight: 600, color: '#d97706' }}>
                ⚠ {warning}
              </p>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: sub, display: 'block', marginBottom: 6 }}>
              Observaciones — opcional
            </label>
            <textarea
              rows={2}
              placeholder="Quién/por qué se cerró delegado, etc."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...input, resize: 'none', lineHeight: 1.5 }} />
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600,
                        background: 'rgba(239,68,68,0.08)', padding: '8px 12px',
                        borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} disabled={saving}
              style={{ flex: 1, padding: '12px', borderRadius: 14,
                       border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                       background: 'transparent', cursor: saving ? 'not-allowed' : 'pointer',
                       fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: sub }}>
              Cancelar
            </button>
            <button onClick={submit} disabled={saving}
              style={{ flex: 2, padding: '12px', borderRadius: 14, border: 'none',
                       background: saving ? '#64748b' : 'linear-gradient(135deg,#16a34a,#15803d)',
                       cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                       fontSize: 13, fontWeight: 700, color: 'white',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              {saving
                ? <><Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Cerrando…</>
                : <><CheckCircle2 style={{ width: 14, height: 14 }} /> Cerrar vuelo</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Modal: anular ───────────────────────────────────────────────
const AnnulModal = ({ flight, darkMode, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const text  = darkMode ? 'white' : '#0f172a';
  const sub   = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const bg    = darkMode ? '#111827' : 'white';
  const input = {
    width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 14,
    fontWeight: 500, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    color: text, resize: 'none', lineHeight: 1.5,
  };

  const submit = async () => {
    setError('');
    if (!reason.trim()) { setError('El motivo es obligatorio'); return; }
    setSaving(true);
    try {
      await hobbsService.annul(flight.id, reason.trim());
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al anular el vuelo');
    } finally { setSaving(false); }
  };

  const acName = `${flight.manufacturer} ${flight.model_name}${flight.registration ? ' (' + flight.registration + ')' : ''}`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)',
               backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center',
               justifyContent: 'center', padding: 16 }}>
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        style={{ width: '100%', maxWidth: 420, borderRadius: 24, background: bg,
                 boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                 border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)' }}>
        <div style={{ padding: '20px 24px 16px',
                      borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14,
                          background: 'linear-gradient(135deg,#94a3b8,#64748b)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ban style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>Anular vuelo</h2>
              <p style={{ margin: 0, fontSize: 12, color: sub }}>{acName}</p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none',
                     borderRadius: 10, width: 34, height: 34, cursor: 'pointer',
                     display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: sub, lineHeight: 1.5 }}>
            El vuelo se va a marcar como <b>anulado</b>: no cuenta para horas del piloto,
            no bloquea la aeronave y queda registrado en el libro con el motivo.
          </p>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: sub, display: 'block', marginBottom: 6 }}>
              Motivo *
            </label>
            <textarea
              rows={3}
              placeholder="Ej: el vuelo se canceló por clima, el piloto no salió a volar"
              value={reason}
              onChange={e => setReason(e.target.value)}
              style={input} />
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600,
                        background: 'rgba(239,68,68,0.08)', padding: '8px 12px',
                        borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} disabled={saving}
              style={{ flex: 1, padding: '12px', borderRadius: 14,
                       border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                       background: 'transparent', cursor: saving ? 'not-allowed' : 'pointer',
                       fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: sub }}>
              Cancelar
            </button>
            <button onClick={submit} disabled={saving}
              style={{ flex: 2, padding: '12px', borderRadius: 14, border: 'none',
                       background: saving ? '#64748b' : 'linear-gradient(135deg,#64748b,#475569)',
                       cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                       fontSize: 13, fontWeight: 700, color: 'white',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              {saving
                ? <><Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Anulando…</>
                : <><Ban style={{ width: 14, height: 14 }} /> Anular vuelo</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Sección historial (solo lectura) ────────────────────────────
const HistorySection = ({ darkMode, history, loading, error }) => {
  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)' : '#64748b';
  const card = {
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border:     darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    borderRadius: 20, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0', color: sub }}>
        <Loader style={{ width: 24, height: 24, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444',
                    fontSize: 13, fontWeight: 600 }}>
        {error}
      </div>
    );
  }
  if (!history.length) {
    return (
      <div style={{ ...card, textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(148,163,184,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px' }}>
          <History style={{ width: 28, height: 28, color: '#94a3b8' }} />
        </div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
          Sin historial todavía
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: sub }}>
          Acá vas a ver los últimos vuelos cerrados y anulados del aeroclub.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 12, color: sub, fontWeight: 600 }}>
        Últimos {history.length} vuelos resueltos (cerrados o anulados).
      </p>
      {history.map((f, i) => {
        const acLabel = `${f.manufacturer} ${f.model_name}`;
        const isAnnulled = f.status === 'annulled';
        const isClosed   = f.status === 'closed';
        const ts = f.closed_at || f.updated_at || f.created_at;
        return (
          <motion.div key={f.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
            style={{
              ...card,
              padding: 16,
              borderLeft: isAnnulled ? '4px solid #94a3b8' : '4px solid #16a34a',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12,
                            background: isAnnulled
                              ? 'linear-gradient(135deg,#94a3b8,#64748b)'
                              : 'linear-gradient(135deg,#16a34a,#15803d)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0 }}>
                {isAnnulled
                  ? <Ban style={{ width: 18, height: 18, color: 'white' }} />
                  : <CheckCircle2 style={{ width: 18, height: 18, color: 'white' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Plane style={{ width: 13, height: 13, color: sub, flexShrink: 0 }} />
                  <span style={{ fontWeight: 800, fontSize: 14, color: text }}>{acLabel}</span>
                  {f.registration && (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', padding: '1px 8px',
                                   borderRadius: 99,
                                   background: darkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                                   color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {f.registration}
                    </span>
                  )}
                  {isClosed && (
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                   padding: '2px 8px', borderRadius: 99,
                                   background: 'rgba(22,163,74,0.15)', color: '#16a34a',
                                   border: '1px solid rgba(22,163,74,0.3)' }}>
                      CERRADO
                    </span>
                  )}
                  {isAnnulled && (
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                   padding: '2px 8px', borderRadius: 99,
                                   background: 'rgba(148,163,184,0.18)', color: '#475569',
                                   border: '1px solid rgba(148,163,184,0.4)' }}>
                      ANULADO
                    </span>
                  )}
                  {isClosed && f.is_delegated_close && (
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                   padding: '2px 8px', borderRadius: 99,
                                   background: 'rgba(37,99,235,0.12)', color: '#2563eb',
                                   border: '1px solid rgba(37,99,235,0.3)' }}>
                      DELEGADO
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 12,
                              color: sub, flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <User style={{ width: 12, height: 12 }} />
                    Piloto: <b style={{ color: text }}>{f.recorded_by_name || '—'}</b>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar style={{ width: 12, height: 12 }} />
                    {formatDateTime(ts)} · {formatRelative(ts)}
                  </span>
                  {isClosed && f.hours_flown != null && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Gauge style={{ width: 12, height: 12 }} />
                      <b style={{ color: text }}>{f.hours_flown} hs</b>
                      <span>(salida {f.hobbs_start ?? '—'} → {f.hobbs_end ?? '—'})</span>
                    </span>
                  )}
                </div>
                {isClosed && f.closed_by_name && (
                  <div style={{ marginTop: 6, fontSize: 12, color: sub,
                                display: 'flex', alignItems: 'center', gap: 4 }}>
                    <UserCheck style={{ width: 12, height: 12 }} />
                    Cerró: <b style={{ color: text }}>{f.closed_by_name}</b>
                    {f.is_delegated_close
                      ? <span style={{ fontStyle: 'italic' }}>(cierre delegado)</span>
                      : <span style={{ fontStyle: 'italic' }}>(cierre propio)</span>}
                  </div>
                )}
                {isAnnulled && f.annulled_reason && (
                  <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 10,
                                background: darkMode ? 'rgba(148,163,184,0.08)' : 'rgba(241,245,249,0.8)',
                                border: darkMode ? '1px solid rgba(148,163,184,0.15)' : '1px solid #e2e8f0',
                                display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <MessageSquare style={{ width: 13, height: 13, color: sub, marginTop: 2, flexShrink: 0 }} />
                    <div style={{ fontSize: 12, color: text, lineHeight: 1.45 }}>
                      <b style={{ color: sub, fontWeight: 700 }}>Motivo:</b> {f.annulled_reason}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ── Vista principal ─────────────────────────────────────────────
const AdminOpenFlights = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const [view, setView]       = useState('open'); // 'open' | 'history'
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [closingFlight, setClosingFlight] = useState(null);
  const [annullingFlight, setAnnullingFlight] = useState(null);

  const [history, setHistory]           = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)' : '#64748b';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res     = await hobbsService.getPending();
      const pending = (res.data?.pending || []).filter(f => f.status === 'open');
      setFlights(pending);
    } catch {
      setError('Error al cargar los vuelos abiertos');
    } finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true); setHistoryError('');
    try {
      const res = await hobbsService.getHistory(50);
      setHistory(res.data?.history || []);
      setHistoryLoaded(true);
    } catch {
      setHistoryError('Error al cargar el historial');
    } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view === 'history' && !historyLoaded) loadHistory();
  }, [view, historyLoaded, loadHistory]);

  const onActionDone = () => {
    setClosingFlight(null);
    setAnnullingFlight(null);
    load();
    // Si el historial ya fue cargado alguna vez, refrescarlo para reflejar
    // el cierre/anulación recién hecho desde esta misma pantalla.
    if (historyLoaded) loadHistory();
  };

  const refresh = () => {
    if (view === 'history') loadHistory();
    else load();
  };

  const card = {
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border:     darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    borderRadius: 20, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  const staleCount = flights.filter(f => minutesSince(f.created_at) >= STALE_MINUTES).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 900, color: text, letterSpacing: '-0.5px' }}>
            {t('open_flights_title', { defaultValue: 'Vuelos abiertos' })}
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: sub }}>
            {t('open_flights_desc', { defaultValue: 'Vuelos sin cerrar de tu organización. Cerralos delegado o anulalos.' })}
          </p>
        </div>
        <motion.button type="button" onClick={refresh} whileTap={{ scale: 0.95 }}
          style={{ padding: 10, borderRadius: 12,
                   border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                   background: 'transparent', cursor: 'pointer', color: sub, display: 'flex' }}>
          <RefreshCw style={{ width: 16, height: 16 }} />
        </motion.button>
      </div>

      {/* Toggle Abiertos / Historial */}
      <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: 4, borderRadius: 12,
                    background: darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>
        {[
          { id: 'open',    label: 'Abiertos',  Icon: Hourglass },
          { id: 'history', label: 'Historial', Icon: History },
        ].map(({ id, label, Icon }) => {
          const active = view === id;
          return (
            <button key={id} type="button" onClick={() => setView(id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6,
                       padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                       fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                       background: active ? (darkMode ? '#1f2937' : 'white') : 'transparent',
                       color: active ? text : sub,
                       boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
              <Icon style={{ width: 14, height: 14 }} /> {label}
            </button>
          );
        })}
      </div>

      {view === 'open' && (<>
      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, padding: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Hourglass style={{ width: 20, height: 20, color: '#f59e0b' }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: text, lineHeight: 1 }}>{flights.length}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, fontWeight: 600, color: sub }}>
              {t('open_flights_summary_open', { defaultValue: 'sin cerrar' })}
            </p>
          </div>
        </div>
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, padding: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(239,68,68,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle style={{ width: 20, height: 20, color: '#ef4444' }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: text, lineHeight: 1 }}>{staleCount}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, fontWeight: 600, color: sub }}>
              {t('open_flights_summary_stale', { defaultValue: 'hace +1 hora' })}
            </p>
          </div>
        </div>
      </div>

      {/* Estado: error */}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444',
                      fontSize: 13, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* Listado */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0', color: sub }}>
          <Loader style={{ width: 24, height: 24, animation: 'spin 1s linear infinite' }} />
        </div>
      ) : flights.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(22,163,74,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 16px' }}>
            <CheckCircle2 style={{ width: 28, height: 28, color: '#16a34a' }} />
          </div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
            {t('open_flights_empty_title', { defaultValue: 'No hay vuelos sin cerrar' })}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: sub }}>
            {t('open_flights_empty_desc', { defaultValue: 'Todos los vuelos del aeroclub están cerrados o anulados.' })}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {flights.map((f, i) => {
            const acLabel = `${f.manufacturer} ${f.model_name}`;
            const mins    = minutesSince(f.created_at);
            const isStale = mins >= STALE_MINUTES;
            return (
              <motion.div key={f.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                style={{
                  ...card,
                  padding: 18,
                  borderLeft: isStale ? '4px solid #f59e0b' : card.border,
                  display: 'flex', flexDirection: 'column', gap: 12,
                }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 14,
                                background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0 }}>
                    <Hourglass style={{ width: 20, height: 20, color: 'white' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Plane style={{ width: 14, height: 14, color: sub, flexShrink: 0 }} />
                      <span style={{ fontWeight: 800, fontSize: 15, color: text }}>{acLabel}</span>
                      {f.registration && (
                        <span style={{ fontSize: 11, fontFamily: 'monospace', padding: '1px 8px',
                                       borderRadius: 99,
                                       background: darkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                                       color: darkMode ? '#94a3b8' : '#64748b' }}>
                          {f.registration}
                        </span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                     padding: '2px 8px', borderRadius: 99,
                                     background: 'rgba(245,158,11,0.15)', color: '#d97706',
                                     border: '1px solid rgba(245,158,11,0.35)' }}>
                        SIN CERRAR
                      </span>
                      {isStale && (
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                       padding: '2px 8px', borderRadius: 99,
                                       background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                                       border: '1px solid rgba(239,68,68,0.3)' }}>
                          +1h ABIERTO
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 12,
                                  color: sub, flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <User style={{ width: 12, height: 12 }} />
                        {f.recorded_by_name || '—'}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Gauge style={{ width: 12, height: 12 }} />
                        Hobbs salida: <b style={{ color: text }}>
                          {f.hobbs_start != null ? `${f.hobbs_start} hs` : '—'}
                        </b>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar style={{ width: 12, height: 12 }} />
                        {formatDateTime(f.created_at)} · {formatRelative(f.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button onClick={() => setAnnullingFlight(f)}
                    style={{ padding: '10px 16px', borderRadius: 12, border: 'none',
                             cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                             background: darkMode ? 'rgba(148,163,184,0.15)' : 'rgba(241,245,249,0.9)',
                             color: darkMode ? '#cbd5e1' : '#475569',
                             display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Ban style={{ width: 14, height: 14 }} /> Anular
                  </button>
                  <button onClick={() => setClosingFlight(f)}
                    style={{ padding: '10px 16px', borderRadius: 12, border: 'none',
                             cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                             background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white',
                             display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle2 style={{ width: 14, height: 14 }} /> Cerrar vuelo
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      </>)}

      {view === 'history' && (
        <HistorySection
          darkMode={darkMode}
          history={history}
          loading={historyLoading}
          error={historyError}
        />
      )}

      <AnimatePresence>
        {closingFlight && (
          <CloseModal
            key={`close-${closingFlight.id}`}
            flight={closingFlight}
            darkMode={darkMode}
            onClose={() => setClosingFlight(null)}
            onDone={onActionDone}
          />
        )}
        {annullingFlight && (
          <AnnulModal
            key={`annul-${annullingFlight.id}`}
            flight={annullingFlight}
            darkMode={darkMode}
            onClose={() => setAnnullingFlight(null)}
            onDone={onActionDone}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOpenFlights;
